import type { DataOption } from '@/entities/column'
import type {
	AnchorResponseModel,
	FieldSchemaModel,
	FilterRuleModel,
} from '@/shared/generated'

import { prop } from 'lodash/fp'
import { Share2Icon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import {
	BasicBadgeField,
	createColumnProcessor,
	useColumnsFields,
} from '@/entities/column'

import { createSRTColumnHelper } from '@/shared/components/master-table'
import { Badge } from '@/shared/components/ui/badge'
import { LinkButton } from '@/shared/components/ui/link-button'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import { useLocationQuery } from '@/shared/hooks/useLocationQuery'
import { ampli } from '@/shared/lib/ampli'
import { useStore } from '@/shared/lib/datasheet/store'

import { useAnchorsManagementContext } from '../ui/AnchorManagementProvider'
import { getAchorTypes, getTableRowId } from './helpers'
import { useColumnChoices } from './useColumnChoices'

export function useColumns() {
	const { t } = useTranslation()
	const schema = useStore((state) => state.schema)
	const [, setQuery] = useLocationQuery()
	const { tableChangesStorage } = useAnchorsManagementContext()
	const dataChoices = useColumnChoices()
	const columnHelper = createSRTColumnHelper<AnchorResponseModel>()
	const setFilters = (filters: FilterRuleModel[]) =>
		setQuery({ filters: filters?.length ? filters : null })
	const { columns } = useColumnsFields({
		schema,
		getChoices: () => dataChoices,
		processor: createColumnProcessor<AnchorResponseModel>(
			{
				columnHelper,
				tableChangesStorage,
				getCell: (schema: FieldSchemaModel) => {
					if (schema.id === 'anchor_type') {
						return AnchorTypeField
					} else if (schema.id === 'alerts') {
						return AlertsCountField
					} else if (schema.id === 'sku') {
						return (props: { value?: string; row: AnchorResponseModel }) => (
							<LinkButton
								variant='brand'
								onClick={() => {
									ampli.anchoringTableSkuClick()
									setQuery({
										selected: getTableRowId(props.row),
									})
								}}
							>
								{props.value}
							</LinkButton>
						)
					} else if (schema.id === 'anchor_count') {
						return (props: {
							value?: number
							field: FieldSchemaModel
							row: AnchorResponseModel
						}) => {
							const anchorType = props.field?.id?.replace('_count', '')

							return props.value ? (
								<LinkButton
									variant='brand'
									onClick={() => {
										ampli.anchoringTableCountClick()
										setFilters([
											{
												name: `${anchorType}_sku`,
												operation: 'is',
												value: props.row.sku!,
											},
										])
									}}
								>
									{props.value}
								</LinkButton>
							) : (
								<span>-</span>
							)
						}
					}

					return null
				},
				getAccessorFn: (schema) => prop(schema.id),
			},
			t
		),
	})

	return columns
}

const AnchorTypeField = ({
	row,
	choices,
	...other
}: {
	row: AnchorResponseModel
	choices: Record<string, DataOption>
	schema: FieldSchemaModel
}) => {
	const contextcChoices = Object.values(choices || {})?.reduce(
		(acc, choice: DataOption) => {
			acc[choice.value as string] = {
				...choice,
				icon:
					(row[
						`${choice.value}_anchor_count` as keyof AnchorResponseModel
					] as number) > 0 ? (
						<Share2Icon className='size-4' />
					) : null,
			}

			return acc
		},
		{} as Record<string, DataOption>
	)

	return (
		<BasicBadgeField
			{...other}
			value={getAchorTypes(row)}
			choices={contextcChoices}
		/>
	)
}
function AlertsCountField({
	value,
	choices,
}: {
	value: string[]
	choices: Record<string, DataOption>
}) {
	const { t } = useTranslation()

	const alerts = value?.filter(Boolean)

	return alerts?.length > 0 ? (
		<Tooltip>
			<TooltipTrigger asChild>
				<Badge className='cursor-help' variant='destructive'>
					{alerts?.length}
				</Badge>
			</TooltipTrigger>
			<TooltipContent>
				<>
					{alerts.map((alertId: string) => {
						const annotation = choices?.[alertId]
						const alertName =
							annotation?.label ||
							t(alertId) ||
							t(`anchor_alerts.${alertId}.name`) ||
							t(`product_alert.${alertId}.name`, { defaultValue: alertId })

						return <div key={alertId}>{alertName}</div>
					})}
				</>
			</TooltipContent>
		</Tooltip>
	) : (
		<span>-</span>
	)
}
